import type { PostHogConfig } from 'posthog-js';
import { sanitizeAppGuardEvent } from './posthog-app-guard-policy';

import {
  createPostHogExceptionBeforeSend,
  POSTHOG_BROWSER_ERROR_CONFIG,
} from './posthog-browser-error-policy';
import { isReplayBlockedPath } from './replay-route-guard';
import {
  redactShareTokens,
  redactShareTokensInRecord,
} from './share-url-redaction';

/**
 * The browser PostHog configuration, and the one piece of its state that
 * before_send never sees (sharing review r7 follow-up): posthog-js stores
 * the first page's URL and referrer as `$initial_person_info` ({u, r}) in
 * its persistence (cookie) on the first capture and sends them with every
 * /flags request. It is written here first, redacted, before any capture
 * (posthog-js only fills it once), and re-redacted on every route change
 * so a token stored by an earlier version is cleaned too. On a capability
 * first page the first flags load waits until the person leaves it.
 */

const INITIAL_PERSON_INFO = '$initial_person_info';

export type InitialPersonInfoClient = {
  get_property(key: string): unknown;
  persistence?: { register(props: Record<string, unknown>): unknown } | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function redacted(value: unknown): unknown {
  return typeof value === 'string' ? redactShareTokens(value) : value;
}

/** Other stored page URLs: the session's entry page ({sessionId, props: {u, r}}). */
const STORED_PAGE_KEYS = ['$client_session_props'] as const;

export function scrubPostHogInitialPersonInfo(
  client: InitialPersonInfoClient,
  page: { href: string; referrer: string }
): void {
  const persistence = client.persistence;
  if (!persistence) return;
  for (const key of STORED_PAGE_KEYS) {
    const value = client.get_property(key);
    if (!isRecord(value)) continue;
    const clean = redactShareTokensInRecord(value);
    if (JSON.stringify(clean) !== JSON.stringify(value))
      persistence.register({ [key]: clean });
  }
  const info = client.get_property(INITIAL_PERSON_INFO);
  if (isRecord(info)) {
    const u = redacted(info.u);
    const r = redacted(info.r);
    if (u !== info.u || r !== info.r)
      persistence.register({ [INITIAL_PERSON_INFO]: { ...info, u, r } });
    return;
  }
  // Not stored yet: store the redacted first page now, so posthog-js (which writes it only once) keeps it.
  persistence.register({
    [INITIAL_PERSON_INFO]: {
      r: redactShareTokens(page.referrer) || '$direct',
      u: redactShareTokens(page.href),
    },
  });
}

export function buildPostHogInitConfig(args: {
  pathname: string;
  apiHost?: string;
  uiHost?: string;
}): Partial<PostHogConfig> {
  const startedBlocked = isReplayBlockedPath(args.pathname);
  let loadedClient: InitialPersonInfoClient | null = null;
  const scrub = () => {
    if (loadedClient)
      scrubPostHogInitialPersonInfo(loadedClient, {
        href: window.location.href,
        referrer: document.referrer,
      });
  };
  return {
    api_host: args.apiHost ?? '/ph-ingest',
    ...(args.uiHost ? { ui_host: args.uiHost } : {}),
    defaults: '2025-05-24',
    person_profiles: 'identified_only',
    capture_pageview: 'history_change',
    capture_pageleave: true,
    ...POSTHOG_BROWSER_ERROR_CONFIG,
    // Every event also re-cleans what posthog-js stored for the session (it may start a new one on any page).
    before_send: [
      (event) => {
        scrub();
        return event;
      },
      ...createPostHogExceptionBeforeSend(),
      sanitizeAppGuardEvent,
    ],
    // Replay never starts on a capability route (r7 #2); nor does the first flags load.
    ...(startedBlocked
      ? {
          disable_session_recording: true,
          advanced_disable_feature_flags_on_first_load: true,
        }
      : {}),
    loaded: (client) => {
      loadedClient = client;
      scrub();
    },
  };
}
